import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { format, subDays, eachDayOfInterval, parseISO, isValid } from 'date-fns';
import { tr } from 'date-fns/locale';
import { TrendingUp, Users, CheckCircle2, MousePointerClick, ExternalLink, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_CONFIG = {
  visited:   { label: 'Ziyaret',    color: 'bg-gray-100 text-gray-600 border-gray-200' },
  started:   { label: 'Başladı',    color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700 border-green-200' },
};

const CHANNEL_META = {
  instagram: { label: 'Sosyal Medya', color: 'bg-pink-100 text-pink-700' },
  web:       { label: 'Web/Google',   color: 'bg-blue-100 text-blue-700' },
  referral:  { label: 'Referans',     color: 'bg-green-100 text-green-700' },
  other:     { label: 'Diğer',        color: 'bg-gray-100 text-gray-600' },
};

const PERIOD_OPTIONS = [
  { label: 'Son 7 gün',  days: 7 },
  { label: 'Son 30 gün', days: 30 },
  { label: 'Son 90 gün', days: 90 },
];

function getChannelKey(event) {
  const val = (event.ref || event.utmSource || '').toLowerCase().trim();
  if (!val) return 'web';
  if (['instagram', 'tiktok', 'facebook', 'twitter'].some((c) => val.includes(c))) return 'instagram';
  if (['google', 'google_ads', 'bing', 'youtube', 'wp', 'wordpress', 'website', 'web'].some((c) => val === c)) return 'web';
  if (val === 'referral') return 'referral';
  return 'other';
}

function getChannelLabel(event) {
  const val = event.ref || event.utmSource || '';
  return val || 'Direkt';
}

function toDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (ts?.toDate) return ts.toDate();
  if (typeof ts === 'string') { const d = parseISO(ts); return isValid(d) ? d : null; }
  return null;
}

export default function AdminFunnel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [expandedChannel, setExpandedChannel] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const since = Timestamp.fromDate(subDays(new Date(), period));
        const q = query(
          collection(db, 'registration_events'),
          where('visitedAt', '>=', since),
          orderBy('visitedAt', 'desc')
        );
        const snap = await getDocs(q);
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('AdminFunnel fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [period]);

  const summary = useMemo(() => {
    const visited = events.length;
    const started = events.filter((e) => e.status === 'started' || e.status === 'completed').length;
    const completed = events.filter((e) => e.status === 'completed').length;
    const rate = visited > 0 ? Math.round((completed / visited) * 100) : 0;
    return { visited, started, completed, rate };
  }, [events]);

  const channelStats = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const key = getChannelKey(e);
      const label = getChannelLabel(e);
      const mapKey = label || key;
      if (!map[mapKey]) map[mapKey] = { channelKey: key, label: mapKey, visited: 0, started: 0, completed: 0 };
      map[mapKey].visited++;
      if (e.status === 'started' || e.status === 'completed') map[mapKey].started++;
      if (e.status === 'completed') map[mapKey].completed++;
    });
    return Object.values(map).sort((a, b) => b.visited - a.visited);
  }, [events]);

  const dailyStats = useMemo(() => {
    const days = eachDayOfInterval({ start: subDays(new Date(), 29), end: new Date() });
    return days.map((day) => {
      const dayStr = format(day, 'yyyy-MM-dd');
      const dayEvents = events.filter((e) => {
        const d = toDate(e.visitedAt);
        return d && format(d, 'yyyy-MM-dd') === dayStr;
      });
      return {
        date: day,
        label: format(day, 'd MMM', { locale: tr }),
        visited: dayEvents.length,
        completed: dayEvents.filter((e) => e.status === 'completed').length,
      };
    });
  }, [events]);

  const maxBar = useMemo(() => Math.max(...dailyStats.map((d) => d.visited), 1), [dailyStats]);

  const eventsForChannel = (channelLabel) =>
    events.filter((e) => getChannelLabel(e) === channelLabel || (channelLabel === 'Direkt' && !e.ref && !e.utmSource));

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-blue-500" />
            Kayıt Kaynakları & Dönüşüm
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">/register sayfası funnel analitikleri</p>
        </div>
        {/* Dönem seçici */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setPeriod(opt.days)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                period === opt.days ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Yükleniyor…</div>
      ) : (
        <>
          {/* Özet Kartları */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Toplam Ziyaret',    value: summary.visited,   icon: Users,            color: 'text-blue-500',   bg: 'bg-blue-50' },
              { label: 'Form Başladı',       value: summary.started,   icon: MousePointerClick, color: 'text-yellow-500', bg: 'bg-yellow-50' },
              { label: 'Tamamlandı',         value: summary.completed, icon: CheckCircle2,      color: 'text-green-500',  bg: 'bg-green-50' },
              { label: 'Dönüşüm Oranı',     value: `${summary.rate}%`, icon: BarChart3,         color: 'text-purple-500', bg: 'bg-purple-50' },
            ].map((card) => (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                  <card.icon className={`h-5 w-5 ${card.color}`} />
                </div>
                <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{card.label}</div>
              </div>
            ))}
          </div>

          {/* Zaman Serisi */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Son 30 Gün</h2>
            <div className="flex items-end gap-px overflow-x-auto pb-2" style={{ height: 80 }}>
              {dailyStats.map((d) => (
                <div key={d.label} className="flex flex-col items-center gap-0.5 flex-1 min-w-[10px] group relative">
                  <div className="w-full flex flex-col justify-end" style={{ height: 64 }}>
                    <div
                      className="w-full bg-gray-200 rounded-t"
                      style={{ height: `${Math.round((d.visited / maxBar) * 60)}px` }}
                    />
                  </div>
                  <div
                    className="absolute bottom-4 w-full bg-green-400 rounded-t opacity-80"
                    style={{ height: `${Math.round((d.completed / maxBar) * 60)}px` }}
                  />
                  <span className="text-[9px] text-gray-400 rotate-45 origin-left mt-1 hidden sm:block">
                    {d.label}
                  </span>
                  {/* Tooltip */}
                  <div className="hidden group-hover:flex absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap z-10 flex-col items-center">
                    <span>{d.label}</span>
                    <span>Ziyaret: {d.visited} · ✓ {d.completed}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200 inline-block" /> Ziyaret</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-400 inline-block" /> Tamamlandı</span>
            </div>
          </div>

          {/* Kanal Bazlı Tablo */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Kanal Bazlı Özet</h2>
            </div>
            {channelStats.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Bu dönemde kayıt yok</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-5 py-3 text-left">Kanal</th>
                    <th className="px-4 py-3 text-right">Ziyaret</th>
                    <th className="px-4 py-3 text-right">Başladı</th>
                    <th className="px-4 py-3 text-right">Tamamlandı</th>
                    <th className="px-4 py-3 text-right">Dönüşüm %</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {channelStats.map((ch) => {
                    const rate = ch.visited > 0 ? Math.round((ch.completed / ch.visited) * 100) : 0;
                    const meta = CHANNEL_META[ch.channelKey] || CHANNEL_META.other;
                    const isExpanded = expandedChannel === ch.label;
                    const channelEvents = eventsForChannel(ch.label);
                    return (
                      <React.Fragment key={ch.label}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => setExpandedChannel(isExpanded ? null : ch.label)}
                        >
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                              <span className="text-gray-700 font-medium">{ch.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">{ch.visited}</td>
                          <td className="px-4 py-3 text-right text-yellow-600">{ch.started}</td>
                          <td className="px-4 py-3 text-right text-green-600 font-medium">{ch.completed}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-semibold ${rate >= 20 ? 'text-green-600' : rate >= 5 ? 'text-yellow-600' : 'text-gray-400'}`}>
                              {rate}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400">
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="bg-gray-50 px-5 py-3">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead className="text-gray-400 uppercase">
                                    <tr>
                                      <th className="py-1 pr-4 text-left">Tarih</th>
                                      <th className="py-1 pr-4 text-left">Kampanya</th>
                                      <th className="py-1 pr-4 text-left">Durum</th>
                                      <th className="py-1 text-left">İşletme</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {channelEvents.slice(0, 20).map((ev) => {
                                      const statusCfg = STATUS_CONFIG[ev.status] || STATUS_CONFIG.visited;
                                      const visitedDate = toDate(ev.visitedAt);
                                      return (
                                        <tr key={ev.id}>
                                          <td className="py-1.5 pr-4 text-gray-500">
                                            {visitedDate ? format(visitedDate, 'dd MMM yyyy HH:mm', { locale: tr }) : '—'}
                                          </td>
                                          <td className="py-1.5 pr-4 text-gray-600">{ev.utmCampaign || '—'}</td>
                                          <td className="py-1.5 pr-4">
                                            <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${statusCfg.color}`}>
                                              {statusCfg.label}
                                            </span>
                                          </td>
                                          <td className="py-1.5">
                                            {ev.businessId ? (
                                              <Link
                                                to="/admin"
                                                className="flex items-center gap-1 text-blue-600 hover:underline"
                                              >
                                                <ExternalLink className="h-3 w-3" />
                                                {ev.businessId.slice(0, 8)}…
                                              </Link>
                                            ) : (
                                              <span className="text-gray-400">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {channelEvents.length > 20 && (
                                      <tr>
                                        <td colSpan={4} className="py-1.5 text-gray-400 text-center">
                                          +{channelEvents.length - 20} daha…
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Tüm Eventler Listesi */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">
                Tüm Kayıtlar
                <span className="ml-2 text-xs font-normal text-gray-400">({events.length})</span>
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-5 py-3 text-left">Tarih</th>
                    <th className="px-5 py-3 text-left">Kaynak</th>
                    <th className="px-5 py-3 text-left">Kampanya</th>
                    <th className="px-5 py-3 text-left">Durum</th>
                    <th className="px-5 py-3 text-left">İşletme</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.slice(0, 100).map((ev) => {
                    const statusCfg = STATUS_CONFIG[ev.status] || STATUS_CONFIG.visited;
                    const visitedDate = toDate(ev.visitedAt);
                    const channelKey = getChannelKey(ev);
                    const meta = CHANNEL_META[channelKey] || CHANNEL_META.other;
                    return (
                      <tr key={ev.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {visitedDate ? format(visitedDate, 'dd MMM yyyy HH:mm', { locale: tr }) : '—'}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                            <span className="text-xs text-gray-600">{ev.ref || ev.utmSource || 'Direkt'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500">{ev.utmCampaign || '—'}</td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${statusCfg.color}`}>
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {ev.businessId ? (
                            <Link to="/admin" className="flex items-center gap-1 text-blue-600 hover:underline text-xs">
                              <ExternalLink className="h-3 w-3" />
                              {ev.businessId.slice(0, 8)}…
                            </Link>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-gray-400 text-sm">
                        Bu dönemde kayıt yok
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
